import { useUserStore } from '~/stores/user'

import type { ChartsData } from '~/types/ChartsPage'
import type { LocationQueryValue } from 'vue-router'

/**
 * Composable for scrolling the track into view.
 *
 * When the route has a "trackId" query param, we need to scroll to the correct track on the page
 * this can be tricky since the track list is virtualized with Recycle Scroller
 * Two things to note:
 *   1. We're using useAsyncData to get all tracks needed so that the data is preloaded before we scroll
 *   2. Since Recycle Scroller only renders a certain number of items, we may need to scroll down to load more items
 *      before we can scroll to the correct track
 */
export function useChartsScrollTrackIntoView(
  trackIdQueryParam: LocationQueryValue | LocationQueryValue[],
  chartsData: globalThis.Ref<ChartsData>,
  handleTrackSelected: (payload: { trackId: number }) => unknown,
  handleMobileChartsPlayerClose: () => void,
) {
  const { IS_LOGGED_IN } = storeToRefs(useUserStore())

  onMounted(() => {
    const trackId = Number(trackIdQueryParam) || 0

    // only scroll if the trackId is a number and the track has been found in the data
    if (trackId && chartsData.value?.foundTrack) {
      handleTrackSelected({ trackId })
      handleMobileChartsPlayerClose()

      // using raf ensures that Recycle Scroller has rendered the list and applied its styling before we check for the element
      requestAnimationFrame(() => {
        const el = document.getElementById(`track-${trackId}`)
        recursivelyScrollElementIntoView(el)
      })
    }
  })

  function recursivelyScrollElementIntoView(el: HTMLElement | null) {
    const { top } = el?.getBoundingClientRect() || { top: 0 }
    const elDistanceFromTop = window.pageYOffset + top

    // the element is visible in the viewport so we can scroll directly to it while offsetting the navbar height
    if (el && elDistanceFromTop >= 0) {
      requestAnimationFrame(() => {
        const DEFAULT_NAV_BAR_HEIGHT = IS_LOGGED_IN.value ? 0 : 76
        const ADDITIONAL_PADDING_OFFSET = 8
        const { height: navbarHeight } = (
          document.querySelector('header') as HTMLElement | null
        )?.getBoundingClientRect() || { height: DEFAULT_NAV_BAR_HEIGHT }
        const offsetPosition =
          elDistanceFromTop - navbarHeight - ADDITIONAL_PADDING_OFFSET
        const scrollingElement =
          document.scrollingElement || document.documentElement

        scrollingElement.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      })

      return
    }

    /*
     * If the element isn't in the viewport, scroll down to where it should be be based on its array index & row height.
     * The Recycle Scroller will load more items so we can pinpoint the element afterwards.
     */
    const MIN_POSSIBLE_ITEMS = 20
    const itemRowHeight = el?.offsetHeight || 78 // 78 is min height of a track item
    const recycleScrollerEl = document.getElementById(
      'charts-track-list',
    ) as HTMLElement | null
    const trackIndexPosition =
      chartsData.value?.results.findIndex(
        (track) => track.track_id === chartsData.value?.foundTrack?.track_id,
      ) || 0
    const listPosFromTop =
      recycleScrollerEl?.getBoundingClientRect().top ?? 0 + window.scrollY
    const scrollHeight =
      (trackIndexPosition > 0 ? trackIndexPosition : MIN_POSSIBLE_ITEMS) *
        itemRowHeight +
      listPosFromTop

    // Using a timeout gives time for the scrolling animation to run and for the Recycle Scroller to load more items
    return setTimeout(() => {
      const scrollingElement =
        document.scrollingElement || document.documentElement
      scrollingElement.scrollTop = scrollingElement.scrollTop + scrollHeight

      return recursivelyScrollElementIntoView(el)
    }, 650)
  }
}
